<script setup>
import { reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  getLilithScenarios,
  createLilithScenario,
  updateLilithScenario,
  deleteLilithScenario,
} from 'dashboard/api/lilithTeam';

const props = defineProps({
  assistantId: { type: String, required: true },
  canManage: { type: Boolean, default: false },
});
const { accountId } = useAccount();
const { t } = useI18n();
const scenarios = ref([]);
const availableTools = ref([]);
const loading = ref(false);
const saving = ref(false);
const error = ref('');
const editing = ref(false);
const editingId = ref('');
const deleteArmed = ref('');
const selectedTool = ref('');
const form = reactive({
  title: '',
  description: '',
  instruction: '',
  enabled: false,
});
let revision = 0;
const errorMessage = e =>
  e?.response?.data?.error ||
  e?.response?.data?.detail ||
  e?.message ||
  t('TEAM_LILITH.SCENARIOS.ERROR');
const load = async () => {
  revision += 1;
  const current = revision;
  loading.value = true;
  error.value = '';
  editing.value = false;
  deleteArmed.value = '';
  try {
    const response = await getLilithScenarios(
      accountId.value,
      props.assistantId,
      props.canManage
    );
    if (current !== revision) return;
    scenarios.value = response.data?.payload || [];
    availableTools.value = response.data?.available_tools || [];
  } catch (e) {
    if (current === revision) {
      scenarios.value = [];
      error.value = errorMessage(e);
    }
  } finally {
    if (current === revision) loading.value = false;
  }
};
watch(() => [props.assistantId, props.canManage, accountId.value], load, {
  immediate: true,
});
const toggle = async item => {
  if (!props.canManage || saving.value) return;
  saving.value = true;
  error.value = '';
  const current = revision;
  try {
    await updateLilithScenario(accountId.value, props.assistantId, item.id, {
      enabled: !item.enabled,
    });
    if (current === revision) item.enabled = !item.enabled;
  } catch (e) {
    if (current === revision) error.value = errorMessage(e);
  } finally {
    if (current === revision) saving.value = false;
  }
};
const edit = item => {
  editingId.value = item?.id || '';
  Object.assign(form, {
    title: item?.title || '',
    description: item?.description || '',
    instruction: item?.instruction || '',
    enabled: item?.enabled ?? false,
  });
  selectedTool.value = '';
  error.value = '';
  editing.value = true;
};
const insertTool = () => {
  if (!selectedTool.value || !props.canManage) return;
  form.instruction += `${form.instruction ? '\n' : ''}[${selectedTool.value}](tool://${selectedTool.value})`;
};
const save = async () => {
  if (!props.canManage || saving.value) return;
  if (
    !form.title.trim() ||
    !form.description.trim() ||
    !form.instruction.trim()
  ) {
    error.value = t('TEAM_LILITH.SCENARIOS.REQUIRED');
    return;
  }
  const current = revision;
  const assistantId = props.assistantId;
  saving.value = true;
  error.value = '';
  try {
    const operation = editingId.value
      ? updateLilithScenario(accountId.value, assistantId, editingId.value, {
          ...form,
        })
      : createLilithScenario(accountId.value, assistantId, { ...form });
    await operation;
    if (current === revision) await load();
  } catch (e) {
    if (current === revision) error.value = errorMessage(e);
  } finally {
    saving.value = false;
  }
};
const remove = async item => {
  if (!props.canManage || saving.value) return;
  if (deleteArmed.value !== item.id) {
    deleteArmed.value = item.id;
    return;
  }
  const current = revision;
  saving.value = true;
  try {
    await deleteLilithScenario(accountId.value, props.assistantId, item.id);
    if (current === revision) await load();
  } catch (e) {
    if (current === revision) error.value = errorMessage(e);
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-1 overflow-y-auto bg-n-surface-1">
    <div class="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-8">
      <header class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ $t('TEAM_LILITH.SCENARIOS.TITLE') }}
          </h1>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ $t('TEAM_LILITH.SCENARIOS.DESCRIPTION') }}
          </p>
        </div>
        <Button
          v-if="canManage"
          :label="$t('TEAM_LILITH.SCENARIOS.CREATE')"
          :disabled="loading || saving"
          @click="edit(null)"
        />
      </header>
      <p
        v-if="error"
        role="alert"
        class="rounded-lg bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
      >
        {{ error }}
      </p>
      <p v-if="loading" class="text-sm text-n-slate-11">
        {{ $t('TEAM_LILITH.SCENARIOS.LOADING') }}
      </p>
      <section
        v-if="editing"
        class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <Input
          v-model="form.title"
          :label="$t('TEAM_LILITH.SCENARIOS.NAME')"
          :disabled="!canManage || saving"
        />
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ $t('TEAM_LILITH.SCENARIOS.TRIGGER') }}
          <textarea
            v-model="form.description"
            rows="3"
            maxlength="500"
            :disabled="!canManage || saving"
            class="rounded-lg border border-n-weak bg-n-surface-1 p-3"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-12">
          {{ $t('TEAM_LILITH.SCENARIOS.INSTRUCTION') }}
          <textarea
            v-model="form.instruction"
            rows="8"
            :disabled="!canManage || saving"
            class="rounded-lg border border-n-weak bg-n-surface-1 p-3"
          />
        </label>
        <div v-if="canManage" class="flex flex-wrap items-center gap-2">
          <select
            v-model="selectedTool"
            :disabled="saving"
            :aria-label="$t('TEAM_LILITH.SCENARIOS.TOOLS')"
            class="rounded-lg border border-n-weak bg-n-surface-1 p-2 text-sm text-n-slate-12"
          >
            <option value="">{{ $t('TEAM_LILITH.SCENARIOS.TOOLS') }}</option>
            <option v-for="tool in availableTools" :key="tool" :value="tool">
              {{ tool }}
            </option>
          </select>
          <Button
            :label="$t('TEAM_LILITH.SCENARIOS.INSERT_TOOL')"
            :disabled="saving || !selectedTool"
            @click="insertTool"
          />
        </div>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            v-model="form.enabled"
            type="checkbox"
            :disabled="!canManage || saving"
          />{{ $t('TEAM_LILITH.SCENARIOS.ENABLED') }}
        </label>
        <p class="text-sm text-n-slate-11">
          {{ $t('TEAM_LILITH.SCENARIOS.ACTIVATION_NOTE') }}
        </p>
        <div class="flex gap-2">
          <Button
            v-if="canManage"
            :label="$t('TEAM_LILITH.SCENARIOS.SAVE')"
            :is-loading="saving"
            @click="save"
          />
          <Button
            :label="$t('TEAM_LILITH.SCENARIOS.CANCEL')"
            :disabled="saving"
            @click="editing = false"
          />
        </div>
      </section>
      <p v-if="!loading && !scenarios.length" class="text-sm text-n-slate-11">
        {{ $t('TEAM_LILITH.SCENARIOS.EMPTY') }}
      </p>
      <article
        v-for="item in scenarios"
        :key="item.id"
        class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-surface-2 p-5"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h2 class="font-semibold text-n-slate-12">{{ item.title }}</h2>
          <button
            v-if="canManage"
            type="button"
            role="switch"
            :aria-checked="item.enabled"
            :aria-label="item.title"
            :disabled="saving"
            class="rounded border border-n-weak px-3 py-1"
            @click="toggle(item)"
          >
            {{
              $t(
                item.enabled
                  ? 'TEAM_LILITH.SCENARIOS.ACTIVE'
                  : 'TEAM_LILITH.SCENARIOS.DISABLED'
              )
            }}
          </button>
          <span class="text-sm text-n-slate-11">{{
            $t(
              item.enabled
                ? 'TEAM_LILITH.SCENARIOS.ACTIVE'
                : 'TEAM_LILITH.SCENARIOS.DISABLED'
            )
          }}</span>
        </div>
        <p class="whitespace-pre-wrap text-sm text-n-slate-11">
          {{ item.description }}
        </p>
        <p class="whitespace-pre-wrap text-sm text-n-slate-12">
          {{ item.instruction }}
        </p>
        <p v-if="item.tools?.length" class="text-xs text-n-slate-10">
          {{ item.tools.join(', ') }}
        </p>
        <div class="flex gap-2">
          <Button
            :label="
              $t(
                canManage
                  ? 'TEAM_LILITH.SCENARIOS.EDIT'
                  : 'TEAM_LILITH.SCENARIOS.VIEW'
              )
            "
            :disabled="saving"
            @click="edit(item)"
          />
          <Button
            v-if="canManage"
            :label="
              $t(
                deleteArmed === item.id
                  ? 'TEAM_LILITH.SCENARIOS.CONFIRM_DELETE'
                  : 'TEAM_LILITH.SCENARIOS.DELETE'
              )
            "
            :disabled="saving"
            @click="remove(item)"
          />
        </div>
      </article>
    </div>
  </div>
</template>
